/**
 * Filter Constants
 *
 * Centralized definitions for all filter options and constants used
 * in the 5-section device filter architecture.
 */

// ═══════════════════════════════════════════════════════════════════
// MAINTENANCE & COMPLIANCE STATUS VALUES (Section 1)
// ═══════════════════════════════════════════════════════════════════

export const MAINTENANCE_STATUSES = {
  OVERDUE: 'Overdue',
  DUE_SOON: 'DueSoon',
  CURRENT: 'Current',
  NOT_SET: 'NotSet'
};

export const MAINTENANCE_STATUS_OPTIONS = [
  { value: MAINTENANCE_STATUSES.OVERDUE, label: '🔴 Overdue', icon: '⚠️' },
  { value: MAINTENANCE_STATUSES.DUE_SOON, label: '🟡 Due Soon', icon: '⏰' },
  { value: MAINTENANCE_STATUSES.CURRENT, label: '🟢 Current', icon: '✓' },
  { value: MAINTENANCE_STATUSES.NOT_SET, label: '⚪ Not Set', icon: '-' }
];

export const CALIBRATION_STATUS_OPTIONS = [
  { value: MAINTENANCE_STATUSES.OVERDUE, label: '🔴 Overdue', icon: '⚠️' },
  { value: MAINTENANCE_STATUSES.DUE_SOON, label: '🟡 Due Soon', icon: '⏰' },
  { value: MAINTENANCE_STATUSES.CURRENT, label: '🟢 Current', icon: '✓' },
  { value: 'NotRequired', label: '⚪ Not Required', icon: '-' }
];

export const COMPLIANCE_STATUS_OPTIONS = [
  { value: MAINTENANCE_STATUSES.OVERDUE, label: '🔴 Overdue', icon: '⚠️' },
  { value: MAINTENANCE_STATUSES.DUE_SOON, label: '🟡 Due Soon', icon: '⏰' },
  { value: MAINTENANCE_STATUSES.CURRENT, label: '🟢 Current', icon: '✓' },
  { value: 'NotRequired', label: '⚪ Not Required', icon: '-' }
];

// ═══════════════════════════════════════════════════════════════════
// WARRANTY & CONTRACT COVERAGE VALUES (Section 2)
// ═══════════════════════════════════════════════════════════════════

export const COVERAGE_STATUSES = {
  EXPIRED: 'Expired',
  EXPIRING_WITHIN_30D: 'ExpiringWithin30d',
  CURRENT: 'Current',
  NOT_COVERED: 'NotCovered'
};

export const COVERAGE_STATUS_OPTIONS = [
  { value: COVERAGE_STATUSES.EXPIRED, label: '🔴 Expired', icon: '❌' },
  { value: COVERAGE_STATUSES.EXPIRING_WITHIN_30D, label: '🟡 Expiring Soon (≤30d)', icon: '⏰' },
  { value: COVERAGE_STATUSES.CURRENT, label: '🟢 Current', icon: '✓' },
  { value: COVERAGE_STATUSES.NOT_COVERED, label: '⚪ Not Covered', icon: '-' }
];

// ═══════════════════════════════════════════════════════════════════
// SERVICE CONTRACT TYPES (Section 3)
// ═══════════════════════════════════════════════════════════════════

export const SERVICE_CONTRACT_TYPES = [
  'Warranty',  // Standard warranty
  'LAMC',      // Labour and Maintenance Contract
  'CAMC',      // Comprehensive Annual Maintenance Contract
  'In-house'   // In-house support
];

export const SERVICE_CONTRACT_TYPE_OPTIONS = [
  { value: 'Warranty', label: 'Warranty', icon: '📜' },
  { value: 'LAMC', label: 'LAMC', icon: '🔧' },
  { value: 'CAMC', label: 'CAMC', icon: '🛠️' },
  { value: 'In-house', label: 'In-house', icon: '🏢' }
];

// ═══════════════════════════════════════════════════════════════════
// DEVICE STATUS VALUES (Section 3)
// ═══════════════════════════════════════════════════════════════════

export const DEVICE_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING_APPROVAL: 'pending_approval',
  IN_TRANSFER: 'in_transfer',
  ARCHIVED: 'archived'
};

export const DEVICE_STATUS_OPTIONS = [
  { value: DEVICE_STATUSES.ACTIVE, label: '🟢 Active', icon: '✓' },
  { value: DEVICE_STATUSES.INACTIVE, label: '🔵 Inactive', icon: '⏸' },
  { value: DEVICE_STATUSES.PENDING_APPROVAL, label: '🟡 Pending Approval', icon: '⏳' },
  { value: DEVICE_STATUSES.IN_TRANSFER, label: '🟠 In Transfer', icon: '🚚' },
  { value: DEVICE_STATUSES.ARCHIVED, label: '⚫ Archived', icon: '📦' }
];

/** Statuses offered on the Devices list filter (draft/pending live in Approvals). */
export const DEVICE_LIST_STATUS_OPTIONS = [
  { value: DEVICE_STATUSES.ACTIVE, label: '🟢 Active', icon: '✓' },
  { value: DEVICE_STATUSES.IN_TRANSFER, label: '🟠 In Transfer', icon: '🚚' },
];

export const DEVICE_LIST_STATUS_LABELS = {
  [DEVICE_STATUSES.ACTIVE]: 'Active',
  [DEVICE_STATUSES.IN_TRANSFER]: 'In Transfer',
};

export const DEVICE_CRITICALITY_VALUES = ['critical', 'major', 'minor'];

export const DEVICE_CRITICALITY_LABELS = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
};

export const DEVICE_CRITICALITY_OPTIONS = [
  { value: 'critical', label: '🔴 Critical', icon: '🔴' },
  { value: 'major', label: '🟠 Major', icon: '🟠' },
  { value: 'minor', label: '🔵 Minor', icon: '🔵' },
];

// ═══════════════════════════════════════════════════════════════════
// YES/NO FILTER OPTIONS (Section 3)
// ═══════════════════════════════════════════════════════════════════

export const YES_NO_OPTIONS = [
  { value: 'Yes', label: '✓ Yes' },
  { value: 'No', label: '✕ No' }
];

export const YES_NO_WITH_ALL = [
  { value: '', label: '◆ All' },
  { value: 'Yes', label: '✓ Yes' },
  { value: 'No', label: '✕ No' }
];

// ═══════════════════════════════════════════════════════════════════
// GLOBAL CONSTANTS
// ═══════════════════════════════════════════════════════════════════

/**
 * Threshold for "expiring soon" (used in 30-day windows)
 * Matches ManagerDashboard.js logic: daysLeft <= 30
 */
export const DAYS_UNTIL_EXPIRY_THRESHOLD = 30;

/**
 * Maximum product cost for range filtering
 * Adjust based on your system's device costs
 */
export const MAX_PRODUCT_COST = 1000000;

/**
 * Minimum product cost for range filtering
 */
export const MIN_PRODUCT_COST = 0;

// ═══════════════════════════════════════════════════════════════════
// FILTER SECTION LABELS & DESCRIPTIONS
// ═══════════════════════════════════════════════════════════════════

export const FILTER_SECTIONS = {
  MAINTENANCE_STATUS: {
    title: '🔧 Maintenance & Compliance Status',
    description: 'Filter by maintenance, calibration, and compliance status',
    icon: '🔧'
  },
  COVERAGE: {
    title: '📋 Warranty & Contract Coverage',
    description: 'Filter by warranty and service contract coverage status',
    icon: '📋'
  },
  CHARACTERISTICS: {
    title: '🏢 Device Characteristics',
    description: 'Filter by device properties and attributes',
    icon: '🏢'
  },
  ISSUES: {
    title: '⚠️ Issues & Complaints',
    description: 'Filter by active complaints and issues',
    icon: '⚠️'
  },
  SEARCH: {
    title: '🔍 Search & Custom Dates',
    description: 'Search devices and filter by installation date',
    icon: '🔍'
  }
};

// ═══════════════════════════════════════════════════════════════════
// FILTER HELP TEXT
// ═══════════════════════════════════════════════════════════════════

export const FILTER_HELP_TEXT = {
  MAINTENANCE_STATUS: {
    ppm: 'Show devices by Preventive Maintenance status. 🔴 Overdue: Due date has passed. 🟡 Due Soon: Due within 30 days.',
    calibration: 'Show devices by Calibration status (if required). 🔴 Overdue: Due date has passed. 🟡 Due Soon: Due within 30 days.',
    compliance: 'Show devices by Compliance status (if required). 🔴 Overdue: Due date has passed. 🟡 Due Soon: Due within 30 days.'
  },
  COVERAGE: {
    warranty: 'Show devices by Warranty coverage status. 🔴 Expired: Warranty has ended. 🟡 Expiring Soon: Will expire within 30 days.',
    contract: 'Show devices by Service Contract status. 🔴 Expired: Contract has ended. 🟡 Expiring Soon: Will expire within 30 days.'
  },
  CHARACTERISTICS: {
    department: 'Filter by device department. Select one or more departments.',
    serviceContractType: 'Filter by service contract type (Warranty, LAMC, CAMC, In-house)',
    deviceStatus: 'Filter by device operational status (Active or In Transfer). Draft and pending approval devices are managed separately.',
    complianceRequired: 'Filter by whether device requires regulatory compliance checks',
    calibrationRequired: 'Filter by whether device requires periodic calibration',
    productCostRange: 'Filter by device purchase cost. Enter min/max values.'
  },
  ISSUES: {
    hasComplaints: 'Show only devices with active complaints or reported issues'
  },
  SEARCH: {
    search: 'Search across Device ID, Name, Serial Number, Model, Manufacturer, and Location',
    installationDate: 'Filter by device installation date range (optional)'
  }
};

// ═══════════════════════════════════════════════════════════════════
// INITIAL FILTER STATE
// ═══════════════════════════════════════════════════════════════════

/**
 * Create initial/empty filter state
 * Useful for reset functionality
 */
export const createInitialFilterState = () => ({
  // Section 1: Maintenance & Compliance Status
  ppmStatus: [],
  calibrationStatus: [],
  complianceStatus: [],

  // Section 2: Warranty & Contract Coverage
  warrantyStatus: [],
  contractStatus: [],

  // Section 3: Device Characteristics
  departments: [],
  serviceContractTypes: [],
  complianceRequired: '',
  calibrationRequired: '',
  productCostFrom: '',
  productCostTo: '',
  deviceStatus: [],

  // Section 4: Issues
  hasActiveComplaints: false,

  // Section 5: Search & Dates
  search: '',
  installationDateFrom: '',
  installationDateTo: ''
});

// ═══════════════════════════════════════════════════════════════════
// FILTER LOGIC PATTERNS
// ═══════════════════════════════════════════════════════════════════

/**
 * Describes which sections use which logic pattern
 * Useful for documentation and understanding filter behavior
 */
export const FILTER_LOGIC_PATTERNS = {
  SECTION_1_MAINTENANCE: {
    section: 1,
    title: 'Maintenance & Compliance Status',
    logic: 'OR',
    description: 'Device matches if ANY selected status condition is true',
    example: 'Show devices that are EITHER [Overdue] OR [Due Soon]'
  },
  SECTION_2_COVERAGE: {
    section: 2,
    title: 'Warranty & Contract Coverage',
    logic: 'OR',
    description: 'Device matches if ANY selected coverage condition is true',
    example: 'Show devices with EITHER [Expired Warranty] OR [No Contract]'
  },
  SECTION_3_CHARACTERISTICS: {
    section: 3,
    title: 'Device Characteristics',
    logic: 'AND',
    description: 'Device must match ALL selected characteristics',
    example: 'Show devices in [Radiology] AND [with Compliance Required]'
  },
  SECTION_4_ISSUES: {
    section: 4,
    title: 'Issues & Complaints',
    logic: 'OR',
    description: 'Simple toggle - show devices with active complaints',
    example: 'Show only devices with active complaints'
  },
  SECTION_5_SEARCH: {
    section: 5,
    title: 'Search & Custom Dates',
    logic: 'AND',
    description: 'All search terms must match AND date must be in range',
    example: 'Search for "X-Ray" AND installation date between 2024-2025'
  }
};
